import type { LocatorLocationAddress } from '../../locator/models/locator.model';

export type ShipmentLanguage = 'en' | 'fr';

/**
 * Customer-selected induction choice on `POST /shipments/create` (OM-774).
 * Distinct from SUCCESS `shipmentInformation.pickupType` (`DROPOFF` | `PRESCHEDULED`).
 */
export type CustomerPickupType = 'CREATE_LABEL' | 'DROP_OFF' | 'PICKUP';

/** Locator address attached when `pickupType` is `DROP_OFF`. */
export type CreateShipmentDropOffInformation = LocatorLocationAddress;

export interface CreateShipmentNotificationPreferences {
  senderReceiveUpdates?: boolean;
  recipientReceiveUpdates?: boolean;
}

export interface CreateShipmentCommonRequest {
  serviceAbilityToken: string;
  packageSelectionToken: string;
  productSelectionToken: string;
  estimateToken: string;
  selectedExpectedDeliveryDate: string;
  accountToken: string;
  /** Required customer induction choice mapped from the Service-step UI method. */
  pickupType: CustomerPickupType;
  /**
   * Selected nearest drop-off locator address when `pickupType` is `DROP_OFF`.
   * Optional when locator search failed — create still proceeds without a location.
   * Omitted for `CREATE_LABEL` and `PICKUP`.
   */
  dropOffInformation?: CreateShipmentDropOffInformation;
  pickupValidationToken?: string;
  references?: Record<string, string>;
  /**
   * Package-step delivery instructions printed in the label Notes field.
   * Kept separate from pickup courier instructions, which go to the pickup request.
   */
  deliveryInstructions?: string;
  notificationPreferences?: CreateShipmentNotificationPreferences;
  language?: ShipmentLanguage;
}

export type CreateShipmentInitialRequest = CreateShipmentCommonRequest;

export interface CreateShipmentContinuationRequest extends CreateShipmentCommonRequest {
  shipmentGenerationToken: string;
  paymentPreloadToken: string;
  checkoutToken: string;
  paymentSuccessPayload: { shipNow: true };
}

export type CreateShipmentRequest =
  CreateShipmentInitialRequest | CreateShipmentContinuationRequest;

export interface CreateShipmentPaymentRequiredResponse {
  status: 'PAYMENT_REQUIRED';
  payment: {
    paymentMethod: 'CREDIT_CARD';
    provider: 'MONERIS';
    preload: {
      providerPayload: { ticket: string };
      attestation: string;
    };
  };
  pins: {
    leadPin: string;
    piecePins: string[];
    attestation: string;
  };
}

/** Document row returned on create SUCCESS under `processing.documents`. */
export interface CreateShipmentDocumentSummary {
  docType: string;
  documentId?: string;
  docURL: string | null;
  status: string;
  failureMessage: string | null;
}

/** Document generation is non-blocking, so `documents` is absent when it failed. */
export interface CreateShipmentDocumentsOutcome {
  status: string;
  documents?: CreateShipmentDocumentSummary[];
  message?: string;
}

/** Card brands the Shipment Lambda may return on credit-card SUCCESS. */
export type PaymentCardBrand = 'VISA' | 'MASTERCARD' | 'AMEX';

/**
 * Formats a payment card brand for accessible image alt text.
 *
 * @param brand Brand code from Shipment Lambda `paymentDisplay`, when present
 */
export function formatPaymentCardBrand(brand: PaymentCardBrand | undefined): string {
  if (brand === 'VISA') {
    return 'Visa';
  }
  if (brand === 'MASTERCARD') {
    return 'Mastercard';
  }
  if (brand === 'AMEX') {
    return 'AMEX';
  }

  return '';
}

/**
 * Resolves the card-brand logo asset for Get Labels / print Payment Method.
 * Returns null when the brand is missing or has no logo asset.
 *
 * @param brand Brand code from Shipment Lambda `paymentDisplay`, when present
 */
export function resolvePaymentCardBrandIconSrc(brand: PaymentCardBrand | undefined): string | null {
  if (brand === 'VISA') {
    return 'assets/icons/visa.svg';
  }
  if (brand === 'MASTERCARD') {
    return 'assets/icons/mastercard.svg';
  }
  if (brand === 'AMEX') {
    return 'assets/icons/amex.svg';
  }

  return null;
}

/** Safe card/billing summary for the confirmation page; absent for account-billed shipments. */
export interface CreateShipmentPaymentDisplay {
  cardBrand?: PaymentCardBrand;
  maskedPan: string;
  expiry: string;
  billingName: string;
  billingPostalCode: string;
}

export interface CreateShipmentSuccessResponse {
  status: 'SUCCESS';
  shipment: {
    shipmentId: string;
    shipmentVersion: number;
    shipmentStatus: string;
    shipmentPin: string;
    shipmentDate: string;
    shipmentInformation: Record<string, unknown>;
  };
  paymentDisplay?: CreateShipmentPaymentDisplay;
  processing: {
    pickup: Record<string, unknown> & { status: string };
    couponRedemption: Record<string, unknown> & { status: string };
    documents: CreateShipmentDocumentsOutcome;
  };
}

export interface CreateShipmentFailedResponse {
  status: 'FAILED';
  shipmentId?: string;
  csopOperationId?: string;
  shipmentStatus?: string;
  error: {
    code: string;
    message: string;
  };
}

export type CreateShipmentResponse =
  | CreateShipmentPaymentRequiredResponse
  | CreateShipmentSuccessResponse
  | CreateShipmentFailedResponse;
